/* global FormData */
import './Attachments.css';
import { useEffect, useRef, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { GET_ATTACHMENTS_FOR_PARENT, useLazyQuery } from 'utils/graphql';
import useToken from 'components/useToken';
import { useTranslation } from 'react-i18next';

const Attachments = ({ parentId, parentTable, readOnly = false }) => {
    const { t } = useTranslation();
    const { token } = useToken();
    const fileInputRef = useRef(null);
    const [attachments, setAttachments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);
    const [viewingId, setViewingId] = useState(null);
    const [uploadError, setUploadError] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewFile, setPreviewFile] = useState(null);
    const [previewFullscreen, setPreviewFullscreen] = useState(false);

    const [getAttachments, { loading: isLoadingAttachments }] = useLazyQuery(
        GET_ATTACHMENTS_FOR_PARENT,
        { fetchPolicy: 'cache-and-network' }
    );

    const loadAttachments = async () => {
        if (!parentId) return;
        const { data } = await getAttachments({
            variables: { parent_id: parentId, parent_table: parentTable },
        });
        if (data?.table?.records) {
            setAttachments(
                data.table.records.map((r) => {
                    const att = r.gc_inventory_attachments;
                    const uploaderName =
                        att.uploaded_by_propertyuser__c_list?.[0]?.propertyuser__c?.name || '';
                    return { ...att, uploaded_by_name: uploaderName };
                })
            );
        }
        setIsLoaded(true);
    };

    useEffect(() => {
        setIsLoaded(false);
        if (parentId) {
            loadAttachments();
        }
    }, [parentId, parentTable]);

    const handleUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        setIsUploading(true);
        setUploadError(null);

        for (const file of files) {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('parent_id', parentId);
            formData.append('parent_table', parentTable);

            try {
                const resp = await fetch(`${process.env.REACT_APP_PROXY_URL || ''}/api/upload`, {
                    method: 'POST',
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                    body: formData,
                });
                if (!resp.ok) {
                    if (resp.status === 413) {
                        setUploadError(t('upload_file_error_size'));
                    } else if (resp.status === 415) {
                        setUploadError(t('upload_file_error_type'));
                    } else {
                        setUploadError(t('upload_file_error'));
                    }
                }
            } catch (error) {
                console.error('Upload failed:', error);
                setUploadError(t('upload_file_error'));
            }
        }

        setIsUploading(false);
        fileInputRef.current.value = '';
        loadAttachments();
    };

    const handleDelete = async (attachmentId) => {
        try {
            await fetch(`${process.env.REACT_APP_PROXY_URL || ''}/api/upload/${attachmentId}`, {
                method: 'DELETE',
                headers: {
                    authorization: `Bearer ${token}`,
                },
            });
            setPendingDeleteId(null);
            loadAttachments();
        } catch (error) {
            console.error('Delete failed:', error);
        }
    };

    const handleViewFile = async (att) => {
        setViewingId(att.id);
        try {
            const resp = await fetch(
                `${process.env.REACT_APP_PROXY_URL || ''}/api/upload/view/${att.id}`,
                { headers: { authorization: `Bearer ${token}` } }
            );
            if (!resp.ok) throw new Error('Failed to get file URL');
            const { signedUrl } = await resp.json();
            setPreviewFile(att);
            setPreviewUrl(signedUrl);
            setPreviewFullscreen(false);
        } catch (error) {
            console.error('View failed:', error);
        }
        setViewingId(null);
    };

    const closePreview = () => {
        setPreviewUrl(null);
        setPreviewFile(null);
        setPreviewFullscreen(false);
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    if (!parentId) {
        return (
            <div className="attachments-section">
                <p className="text-muted mb-0">{t('save_before_uploading')}</p>
            </div>
        );
    }

    const isImage = previewFile?.content_type?.startsWith('image/');

    return (
        <>
            <Modal
                show={!!previewUrl}
                onHide={closePreview}
                fullscreen={previewFullscreen}
                size={previewFullscreen ? undefined : 'xl'}
                centered
            >
                <Modal.Header>
                    <Modal.Title className="text-truncate me-2" style={{ maxWidth: '60%' }}>
                        {previewFile?.file_name}
                    </Modal.Title>
                    <div className="d-flex align-items-center gap-2 ms-auto">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setPreviewFullscreen((f) => !f)}
                            title={previewFullscreen ? t('exit_fullscreen') : t('fullscreen')}
                        >
                            {previewFullscreen ? '⤓' : '⤢'}
                        </Button>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            href={previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            as="a"
                            title={t('open_in_new_tab')}
                        >
                            ↗
                        </Button>
                        <Button variant="outline-secondary" size="sm" onClick={closePreview}>
                            ✕
                        </Button>
                    </div>
                </Modal.Header>
                <Modal.Body className="p-0 attachment-preview-body">
                    {isImage ? (
                        <img
                            src={previewUrl}
                            alt={previewFile?.file_name}
                            className="attachment-preview-img"
                        />
                    ) : (
                        <iframe
                            src={previewUrl}
                            title={previewFile?.file_name}
                            className="attachment-preview-iframe"
                        />
                    )}
                </Modal.Body>
            </Modal>
            <div className="attachments-section">
                <h6 className="mb-2">{t('attachments')}</h6>
                {isLoadingAttachments ? (
                    <div className="d-flex justify-content-center my-2">
                        <Spinner size="sm" />
                    </div>
                ) : (
                    <>
                        {attachments.length === 0 && (
                            <p className="text-muted mb-2">{t('no_attachments')}</p>
                        )}
                        {attachments.map((att) => (
                            <div key={att.id} className="attachment-item">
                                <div className="attachment-info">
                                    <button
                                        className="attachment-name btn btn-link p-0"
                                        title={att.file_name}
                                        onClick={() => handleViewFile(att)}
                                        disabled={viewingId === att.id}
                                    >
                                        {viewingId === att.id ? (
                                            <Spinner size="sm" className="me-1" />
                                        ) : null}
                                        {att.file_name}
                                    </button>
                                    <span className="attachment-meta">
                                        {formatFileSize(att.file_size)}
                                        {att.uploaded_by_name && ` — ${att.uploaded_by_name}`}
                                    </span>
                                </div>
                                {!readOnly &&
                                    (pendingDeleteId === att.id ? (
                                        <div className="d-flex align-items-center gap-1">
                                            <span className="text-danger small me-1">
                                                {t('confirm_delete_attachment')}
                                            </span>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleDelete(att.id)}
                                            >
                                                {t('confirm')}
                                            </Button>
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => setPendingDeleteId(null)}
                                            >
                                                {t('cancel')}
                                            </Button>
                                        </div>
                                    ) : (
                                        <Button
                                            variant="outline-danger"
                                            size="sm"
                                            onClick={() => setPendingDeleteId(att.id)}
                                        >
                                            {t('delete')}
                                        </Button>
                                    ))}
                            </div>
                        ))}
                    </>
                )}
                {!readOnly && (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            onChange={handleUpload}
                            style={{ display: 'none' }}
                        />
                        <Button
                            variant="outline-primary"
                            size="sm"
                            className="mt-2"
                            onClick={() => fileInputRef.current.click()}
                            disabled={isUploading}
                        >
                            {isUploading ? <Spinner size="sm" className="me-1" /> : null}
                            {t('upload_file')}
                        </Button>
                        {uploadError && (
                            <p className="text-danger small mt-1 mb-0">{uploadError}</p>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default Attachments;
